import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ExperienceDistributionItem } from '../../types/reports';
import AccessibleChartSummary from './AccessibleChartSummary';

export default function ExperienceChart({
  data,
}: {
  data: ExperienceDistributionItem[];
}) {
  const colors = [
    '#2563EB',
    '#7C3AED',
    '#0891B2',
    '#EA580C',
    '#16A34A',
    '#DB2777',
  ];

  // Always show these experience ranges
  const experienceRanges = [
    '0-2 Years',
    '3-5 Years',
    '6-10 Years',
    '10+ Years',
  ];

  // Add missing ranges with count 0
  const chartData: ExperienceDistributionItem[] = experienceRanges.map((range) => {
    const existingItem = data.find((item) => item.range === range);

    return existingItem ?? {
      range,
      count: 0,
    };
  });

  const chartWidth = Math.max(520, chartData.length * 130);

  const maxCount = Math.max(
    ...chartData.map((item) => item.count),
    0
  );

  const yAxisMax = Math.max(
    10,
    Math.ceil(maxCount / 10) * 10
  );

  const yAxisTicks = Array.from(
    { length: yAxisMax / 10 + 1 },
    (_, index) => index * 10
  );

  return (
    <>
      <div className="overflow-x-auto pb-2">
        <div style={{ width: chartWidth }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              margin={{
                top: 10,
                right: 16,
                left: 0,
                bottom: 10,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
              />

              <XAxis
                dataKey="range"
                tick={{ fontSize: 12 }}
                interval={0}
              />

              <YAxis
                allowDecimals={false}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
              />

              <Tooltip
                formatter={(value) => [
                  `${value} people`,
                  'Count',
                ]}
              />

              <Bar
                dataKey="count"
                name="People"
                radius={[6, 6, 0, 0]}
              >
                {chartData.map((item, index) => (
                  <Cell
                    key={item.range}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {chartData.map((item, index) => (
          <div
            key={item.range}
            className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2 text-slate-700">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{
                  backgroundColor: colors[index % colors.length],
                }}
                aria-hidden="true"
              />

              <span className="truncate">
                {item.range}
              </span>
            </span>

            <span className="shrink-0 font-semibold text-slate-900">
              {item.count}
            </span>
          </div>
        ))}
      </div>

      <AccessibleChartSummary
        caption="Experience distribution"
        categoryHeader="Experience range"
        valueHeader="People"
        rows={chartData.map((item) => ({
          label: item.range,
          value: item.count,
        }))}
      />
    </>
  );
}